import { MerchantKnowledgeSuggestionStatus } from "@redotech/crdb-enums/agent-knowledge/merchant-knowledge-suggestion-status";
import { SupportTicketAssigneeType } from "@redotech/crdb-enums/shopper-ai/support-ticket-assignee-type";
import { SupportTicketPriority } from "@redotech/crdb-enums/shopper-ai/support-ticket-priority";
import { SupportTicketStatus } from "@redotech/crdb-enums/shopper-ai/support-ticket-status";
import { accountIdFormat } from "@redotech/redo-account-id";
import {
  MongoConversationId,
  MongoUserObjectId,
} from "@redotech/redo-ids-mongo";
import {
  merchantKnowledgeIdFormat,
  suggestedMerchantKnowledgeIdFormat,
} from "@redotech/shopper-agent-knowledge-id";
import * as z from "zod";
import { topicIdSchema } from "./conversation-id";
import { retrievalKeySchema } from "./merchant-knowledge-view";

/** What the generator proposes. Same shape a merchant would author. */
export const suggestionDraftSchema = z.object({
  title: z.string(),
  content: z.string(),
});
export type SuggestionDraft = z.infer<typeof suggestionDraftSchema>;

export const suggestionReviewSchema = z.object({
  status: z.enum(MerchantKnowledgeSuggestionStatus),
  reviewedAt: z.date().nullable(),
  /** Hex ObjectId of the reviewing user. */
  reviewedByMongoUserId: MongoUserObjectId.nullable(),
  /** The article this became, once accepted. */
  acceptedMerchantKnowledgeId: merchantKnowledgeIdFormat.schema.nullable(),
});
export type SuggestionReview = z.infer<typeof suggestionReviewSchema>;

export const merchantKnowledgeSuggestionViewSchema = z.object({
  id: suggestedMerchantKnowledgeIdFormat.schema,
  accountId: accountIdFormat.schema,
  title: z.string(),
  content: z.string(),
  /** Derived the same way an article's is, so dedup compares like with like. */
  retrievalKey: retrievalKeySchema,
  review: suggestionReviewSchema,
  createdAt: z.date(),
  updatedAt: z.date(),
});
export type MerchantKnowledgeSuggestionView = z.infer<
  typeof merchantKnowledgeSuggestionViewSchema
>;

export const merchantKnowledgeSuggestionListItemSchema =
  merchantKnowledgeSuggestionViewSchema.extend({ sourceCount: z.number() });
export type MerchantKnowledgeSuggestionListItem = z.infer<
  typeof merchantKnowledgeSuggestionListItemSchema
>;

export const suggestionSourceLinkSchema = z.object({
  /** Doubles as the Support V2 route id when `hasSupportTicket`. */
  topicId: topicIdSchema,
  hasSupportTicket: z.boolean(),
  /** V1 conversation the topic came from, for stores still on the old inbox. */
  legacyMongoConversationId: MongoConversationId.schema.nullable(),
  ticketNumber: z.number().int().nullable(),
  status: z.enum(SupportTicketStatus).nullable(),
  lastCustomerMessageAt: z.date().nullable(),
  priority: z.enum(SupportTicketPriority).nullable(),
  assigneeType: z.enum(SupportTicketAssigneeType).nullable(),
  mongoAssigneeId: MongoUserObjectId.nullable(),
  createdAt: z.date().nullable(),
});
export type SuggestionSourceLink = z.infer<typeof suggestionSourceLinkSchema>;
